import { Form } from "@workspace/web-ui/components/form"
import { usePrescription } from "../../hooks/use-prescription"
import FormField from "~/features/dashboard/components/form-field/form-field"
import { FormFieldType } from "~/features/dashboard/types"
import { Button } from "@workspace/web-ui/components/button"
import { useState } from "react"
import { Loader } from "lucide-react"
import { PrescriptionType } from "../../types"
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select"

type CustomRenderArgs<T extends FieldValues> = {
    field: ControllerRenderProps<T, Path<T>>;
    error?: string;
};

// Add types and pricing data
interface DosageOption {
    dosage: string;
    price: number;
}

type MedicationKey = "Compounded Semaglutide" | "Compounded Tirzepatide" | "Zepbound (Branded Tirzepatide)";

const medicationPricing: Record<MedicationKey, DosageOption[]> = {
    "Compounded Semaglutide": [
        { dosage: "0.25 mg", price: 199.99 },
        { dosage: "0.5 mg", price: 249.99 },
        { dosage: "0.75 mg", price: 299.99 },
        { dosage: "1 mg", price: 349.99 },
        { dosage: "12.5 mg", price: 550 },
        { dosage: "15 mg", price: 600 },
    ],
    "Compounded Tirzepatide": [
        { dosage: "2.5 mg", price: 249.99 },
        { dosage: "5 mg", price: 299.99 },
        { dosage: "7.5 mg", price: 399.99 },
        { dosage: "10 mg", price: 499.99 },
        { dosage: "12.5 mg", price: 550 },
        { dosage: "15 mg", price: 600 },
    ],
    "Zepbound (Branded Tirzepatide)": [
        { dosage: "2.5 mg", price: 349.99 },
        { dosage: "5 mg", price: 499.99 },
        { dosage: "7.5 mg", price: 599.99 },
        { dosage: "10 mg", price: 699.99 },
        // { dosage: "12.5 mg", price: 550 },
        // { dosage: "15 mg", price: 600 },
    ]
};

const vitaminOptions = [
    { value: "Vitamin B6", label: "Vitamin B6" },
    { value: "Vitamin B12", label: "Vitamin B12" },
    { value: "L-Carnitine", label: "L-Carnitine" },
];

// Helper functions
const getAvailableDosages = (medication: string): DosageOption[] => {
    return medicationPricing[medication as MedicationKey] || [];
};

const getPriceForDosage = (medication: string, dosage: string): string => {
    const options = medicationPricing[medication as MedicationKey] || [];
    const option = options.find(opt => opt.dosage === dosage);
    return option ? option.price.toString() : "";
};

export const PrescriptionForm = ({ patientId, encounterId, refillId, prescriptionId, onClose }: { patientId: string, encounterId: string, refillId: string, prescriptionId: string, onClose?: () => void }) => {
    const { form, handleSubmit, isPending } = usePrescription(encounterId, refillId, prescriptionId, onClose)
    const [durationError, setDurationError] = useState<string>("")

    const medicationOptions = [
        { value: "Compounded Semaglutide", label: "Compounded Semaglutide" },
        { value: "Compounded Tirzepatide", label: "Compounded Tirzepatide" },
        { value: "Zepbound (Branded Tirzepatide)", label: "Zepbound (Branded Tirzepatide)" },
    ];

    // Watch the current medication value to get available dosages
    const currentMedication = form.watch("medication");
    const availableDosages = getAvailableDosages(currentMedication);

    // Create dosage options for the select
    // const dosageOptions = availableDosages.map(option => ({
    //     value: option.dosage,
    //     label: `${option.dosage} - $${option.price}`
    // }));

    // Custom render for dosage field
    const renderDosageField = ({ field }: CustomRenderArgs<PrescriptionType>) => {
        return (
            <Select
                value={String(field.value || '')}
                onValueChange={(selectedDosage) => {
                    field.onChange(selectedDosage);

                    // Auto-populate price when dosage is selected
                    if (currentMedication && selectedDosage) {
                        const autoPrice = getPriceForDosage(currentMedication, selectedDosage);
                        form.setValue("cost", Number(autoPrice));
                    }
                }}
                disabled={!currentMedication}
            >
                <SelectTrigger className="button-primary h-full !font-normal w-full border border-gray-200 rounded-lg">
                    <SelectValue placeholder="Select dosage" />
                </SelectTrigger>
                <SelectContent>
                    {availableDosages.map((option) => (
                        <SelectItem key={option.dosage} value={option.dosage}>
                            {option.dosage}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    };

    // Custom render for duration field
    const renderDurationField = ({ field }: CustomRenderArgs<PrescriptionType>) => {
        return (
            <div className="relative w-full">
                <input
                    id={field.name}
                    name={field.name}
                    ref={field.ref}
                    value={field.value?.toString() || ''}
                    onChange={e => {
                        const value = e.target.value;
                        const isNumber = /^\d*$/.test(value);
                        field.onChange(value);
                        setDurationError(isNumber ? '' : 'Duration must be a number');
                    }}
                    onBlur={field.onBlur}
                    type="text"
                    inputMode="numeric"
                    className={`button-primary h-[36px] !font-normal w-full border border-gray-200 rounded-lg pr-16 ${durationError ? 'aria-invalid focus-visible:border-none focus-visible:ring-[#FF3B3B] focus-visible:ring-[2px]' : ''}`}
                    placeholder="e.g 3"
                />
                <span className="absolute top-0 mt-2 mr-5 right-4 text-sm text-gray-500">Days</span>
                <p
                    className="text-[#FF3B3B] text-xs mt-1 duration-warning"
                    style={{ visibility: durationError ? 'visible' : 'hidden', minHeight: '1.25em' }}
                >
                    {durationError}
                </p>
            </div>
        )
    }

    const renderVitaminField = ({ field }: CustomRenderArgs<PrescriptionType>) => {
        return (
            <Select
                value={String(field.value || "")}
                onValueChange={(selectedVitamin) => field.onChange(selectedVitamin)}
            >
                <SelectTrigger className="button-primary h-full !font-normal w-full border border-gray-200 rounded-lg">
                    <SelectValue placeholder="Select vitamin" />
                </SelectTrigger>
                <SelectContent>
                    {vitaminOptions.map((vit) => (
                        <SelectItem key={vit.value} value={vit.value}>
                            {vit.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    };


    const onAddMedication = async (data: PrescriptionType) => {
        // Format duration as '1 day' or 'N days'
        let duration = data.duration
        const num = Number(duration)
        if (!isNaN(num) && duration && duration.trim() !== "") {
            duration = `${num} ${num === 1 ? "day" : "days"}`
        }
        // Convert cost to number
        const cost = Number(data.cost)
        await handleSubmit({ ...data, duration, cost, patient_id: patientId })
    }

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onAddMedication)}
                className="flex flex-col h-full">
                <div className="px-6 pt-6">
                    <h2 className="text-2xl font-medium mb-3">Add Prescription</h2>
                </div>
                <div className="flex-1 overflow-auto px-6 pb-4">
                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="medication"
                            fieldType={FormFieldType.SELECT}
                            label="Medication"
                            className="w-full border border-gray-200"
                            placeholder="Select..."
                            options={medicationOptions}
                            error={form.formState.errors.medication?.message}
                        />
                        <FormField
                            control={form.control}
                            name="dosage"
                            fieldType={FormFieldType.INPUT}
                            label="Dosage"
                            className="w-full border border-gray-200"
                            placeholder="Select medication first"
                            customRender={renderDosageField}
                            error={form.formState.errors.dosage?.message}
                        />

                        {/* <FormField 
                          control={form.control}
                          name="refills"
                          fieldType={FormFieldType.INPUT}
                          label="Refills"
                          className="w-full border border-gray-200"
                          placeholder="e.g 2"
                          error={form.formState.errors.refills?.message}
                        /> */}
                        
                        {currentMedication !== "Zepbound (Branded Tirzepatide)" && (
                            <FormField
                                control={form.control}
                                name="vitamin.name"
                                fieldType={FormFieldType.SELECT}
                                label="Vitamin"
                                className="w-full border border-gray-200"
                                placeholder="Select..."
                                customRender={renderVitaminField}
                                error={form.formState.errors.vitamin?.name?.message}
                            />
                        )}

                        <div className="flex gap-4 w-full">
                            <div className="w-full">
                            <FormField
                                control={form.control}
                                name="cost"
                                fieldType={FormFieldType.INPUT}
                                label="Price"
                                className="w-full border border-gray-200"
                                placeholder="$ 23"
                                type="number"
                                error={form.formState.errors.cost?.message}
                            />
                            </div>
                            <div className="w-full">
                            <FormField
                                control={form.control}
                                name="duration"
                                fieldType={FormFieldType.INPUT}
                                label="Duration"
                                className="w-full border border-gray-200"
                                placeholder="e.g 3"
                                customRender={renderDurationField}
                                error={form.formState.errors.duration?.message}
                            />
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name="note"
                            fieldType={FormFieldType.TEXTAREA}
                            label="Instructions for medication"
                            className="w-full border border-gray-200"
                            placeholder="Provide instructions for the patient on how to take their medication."
                            error={form.formState.errors.note?.message}
                        />
                    </div>
                </div>
                <div className="flex justify-end w-full gap-2 pt-4 pb-6 px-6 bg-white border-t rounded-b-3xl mt-auto">
                    <Button className="button-primary w-[30%] h-[49px]" type="button">
                        Cancel
                    </Button>
                    {isPending ? (
                        <Button type="submit" className="button-primary h-[49px] !bg-[#427DDD] !border-none !text-white !rounded-lg !shadow-none w-full" disabled>
                            <Loader />
                        </Button>
                    ) : (
                        <Button type="submit" className="button-primary h-[49px] !bg-[#427DDD] !border-none !text-white !rounded-lg !shadow-none w-full">
                            Add medication
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    )
}